import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { clsx } from 'clsx';

export interface CookieBannerProps {
  /** Whether the banner is visible */
  isVisible?: boolean;
  /** Callback when user accepts all cookies */
  onAcceptAll?: () => void;
  /** Callback when user rejects non-essential cookies */
  onRejectAll?: () => void;
  /** Callback when user wants to customize preferences */
  onCustomize?: () => void;
  /** Custom className for styling */
  className?: string;
  /** Position of the banner */
  position?: 'top' | 'bottom';
  /** Theme variant */
  variant?: 'light' | 'dark';
}

const COOKIE_CONSENT_KEY = 'relay_protocol-cookie-consent';
const COOKIE_PREFERENCES_KEY = 'relay_protocol-cookie-preferences';

export const CookieBanner: React.FC<CookieBannerProps> = ({
  isVisible: controlledVisibility,
  onAcceptAll,
  onRejectAll,
  onCustomize,
  className,
  position = 'bottom',
  variant = 'light'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if consent has already been given
    const existingConsent = Cookies.get(COOKIE_CONSENT_KEY);
    if (controlledVisibility !== undefined) {
      setIsVisible(controlledVisibility);
    } else {
      setIsVisible(!existingConsent);
    }
  }, [controlledVisibility]);

  const handleAcceptAll = async () => {
    setIsLoading(true);

    const preferences = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString()
    };

    // Set cookies with 1 year expiration
    Cookies.set(COOKIE_CONSENT_KEY, 'accepted', { expires: 365, sameSite: 'strict' });
    Cookies.set(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences), { expires: 365, sameSite: 'strict' });

    // Track consent event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted',
        functionality_storage: 'granted',
        personalization_storage: 'granted'
      });
    }

    setIsVisible(false);
    setIsLoading(false);
    onAcceptAll?.();
  };

  const handleRejectAll = async () => {
    setIsLoading(true);

    const preferences = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString()
    };

    Cookies.set(COOKIE_CONSENT_KEY, 'rejected', { expires: 365, sameSite: 'strict' });
    Cookies.set(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences), { expires: 365, sameSite: 'strict' });

    // Update consent for analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied'
      });
    }

    setIsVisible(false);
    setIsLoading(false);
    onRejectAll?.();
  };

  const handleCustomize = () => {
    onCustomize?.();
  };

  if (!isVisible) return null;

  return (
    <div
      className={clsx(
        'cookie-banner',
        'fixed left-0 right-0 z-50 p-4 shadow-lg border-t',
        position === 'top' ? 'top-0 border-b' : 'bottom-0 border-t',
        variant === 'dark'
          ? 'bg-gray-900 text-white border-gray-700'
          : 'bg-white text-gray-900 border-gray-200',
        className
      )}
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <h3
              id="cookie-banner-title"
              className="text-lg font-semibold mb-2"
            >
              Cookie Consent
            </h3>
            <p
              id="cookie-banner-description"
              className={clsx(
                'text-sm leading-relaxed',
                variant === 'dark' ? 'text-gray-300' : 'text-gray-600'
              )}
            >
              We use cookies to enhance your experience, analyze site usage, and provide personalized content.
              By continuing to use our site, you consent to our use of cookies in accordance with our{' '}
              <a
                href="/privacy-policy"
                className={clsx(
                  'underline hover:no-underline',
                  variant === 'dark' ? 'text-blue-400' : 'text-blue-600'
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 min-w-max">
            <button
              onClick={handleCustomize}
              disabled={isLoading}
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-md border transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                variant === 'dark'
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-800 focus:ring-gray-500'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500',
                isLoading && 'opacity-50 cursor-not-allowed'
              )}
            >
              Customize
            </button>

            <button
              onClick={handleRejectAll}
              disabled={isLoading}
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-md border transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                variant === 'dark'
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-800 focus:ring-gray-500'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500',
                isLoading && 'opacity-50 cursor-not-allowed'
              )}
            >
              Reject All
            </button>

            <button
              onClick={handleAcceptAll}
              disabled={isLoading}
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                variant === 'dark'
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700',
                isLoading && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isLoading ? 'Saving...' : 'Accept All'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;